// Shared client-side cache for the context registry.
//
// Hits `GET /api/contexts/registry` once per session and serves the same
// snapshot to every consumer (Rule Builder's AdvancedConditions, the
// Simulator's guided form, the Recommendation page's advanced mode).
//
// Why a hook + module-level memo:
//   • Avoids duplicate network round-trips when 3+ surfaces mount on the
//     same page (e.g. Rule Builder + Simulator share an Admin route).
//   • Keeps each consumer's local state simple — no prop drilling.
//   • A `nonce` invalidation hook lets the Rule Builder force a refresh
//     after admins inline-create new portal/subcategory values, so the
//     Simulator/Recommendation surface picks them up immediately.
//
// Shape returned (mirrors the backend response):
//   { definitions: { portal_channel: [...], subcategory: [...], ... },
//     types: ['portal_channel', 'subcategory', ...],
//     total: N }

import { useEffect, useRef, useState } from 'react';
import { AdminAPI } from '@/lib/admin-api';

// Module-scoped cache. Resets only on full page reload.
let _cache = null;          // last successful registry payload
let _inflight = null;       // dedupes concurrent fetches
let _subscribers = new Set();

const _notify = () => {
  for (const cb of _subscribers) {
    try { cb(); } catch (_) { /* swallow */ }
  }
};

async function _load() {
  if (_inflight) return _inflight;
  _inflight = AdminAPI.contexts.registry()
    .then((r) => {
      _cache = {
        definitions: r?.definitions || {},
        types: r?.types || Object.keys(r?.definitions || {}),
        total: r?.total || 0,
        fetchedAt: Date.now(),
      };
      _notify();
      return _cache;
    })
    .catch((err) => {
      // Leave _cache untouched (last good snapshot remains usable).
      console.warn('useContextRegistry: load failed', err);
      throw err;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

/**
 * Force a refetch. Useful after the Rule Builder's inline "+ Add value"
 * promotes a new value to the registry — without this, the Simulator
 * wouldn't see the new chip until the next full page reload.
 */
export function invalidateContextRegistry() {
  _cache = null;
  _load().catch(() => {});
}

/**
 * Read-only hook. Returns { registry, loading, error, refetch }.
 * Components that just want the snapshot can ignore the function refs.
 */
export function useContextRegistry() {
  const [snapshot, setSnapshot] = useState(_cache);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!_cache);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    const subscriber = () => {
      if (!mounted.current) return;
      setSnapshot(_cache);
      setLoading(false);
    };
    _subscribers.add(subscriber);

    if (!_cache) {
      setLoading(true);
      _load()
        .then(() => mounted.current && setError(null))
        .catch((e) => mounted.current && setError(e));
    }

    return () => {
      mounted.current = false;
      _subscribers.delete(subscriber);
    };
  }, []);

  return {
    registry: snapshot,
    loading,
    error,
    refetch: () => {
      _cache = null;
      return _load().catch(() => null);
    },
  };
}

/**
 * Convenience getter for a single context-type definition list. Returns
 * an empty array (never undefined) so consumers can map without
 * defensive checks.
 */
export function getDefinitions(registry, contextType) {
  return registry?.definitions?.[contextType] || [];
}
